import ApiClient from 'api-client/ApiClient'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import ActionsSet from 'core/actions/ActionsSet'
import ListAction from 'core/actions/ListAction'
import UpdateAction from 'core/actions/UpdateAction'
import DeleteAction from 'core/actions/DeleteAction'
import CustomAction from 'core/actions/CustomAction'
import CreateAction from 'core/actions/CreateAction'
import store from 'app/store'
import { cacheActions } from 'core/caching/cacheReducers'
import { listImages } from 'openstack/components/images/actions'
import { listPorts } from 'openstack/components/networks/actions'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import moment from 'moment'
import { sessionActions } from 'core/session/sessionReducers'
import { Server } from './model'
import { listSimpleVolumes } from 'openstack/components/storage/volumes/actions'
import { listVolumeSnapshots } from 'app/plugins/openstack/components/storage/snapshots/actions'
import {
  listUsers,
  listFederatedDomainUsers,
} from 'account/components/userManagement/users/new-actions'
import { listClusterHostGroups } from 'openstack/components/infrastructure/clusters/actions'
import { listServerGroups } from 'openstack/components/vms/server-groups/actions'
import { listLeases, listLeasePolicies } from 'openstack/components/vms/leases/actions'
import { getLastRefreshTime } from 'openstack/helpers'

const { dispatch } = store
const { nova } = ApiClient.getInstance()

const getCurrentNovaFormattedTimestamp = () => {
  return moment()
    .utc()
    .format()
}

export const getNovaVersion = () => {
  return nova.getVersionDetails()
}

const updateVmCachesSingle = (vm) => {
  const state = store.getState()
  const currentUser = state.session?.username
  const userId = state.session?.userDetails?.userId
  const currentTenant = state.preferences?.[currentUser]?.root?.currentTenant
  const isTenantVm = vm?.tenant_id === currentTenant
  const isUserVm = vm?.user_id === userId

  if (isAdmin()) {
    dispatch(
      cacheActions.updateItem({
        uniqueIdentifier: 'id',
        cacheKey: DataKeys.AllOpenstackVirtualMachines,
        params: { id: vm?.id },
        item: vm,
      }),
    )
    if (isTenantVm) {
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id: vm?.id },
          item: vm,
        }),
      )
    }
  } else {
    dispatch(
      cacheActions.updateItem({
        uniqueIdentifier: 'id',
        cacheKey: DataKeys.OpenstackVirtualMachines,
        params: { id: vm?.id },
        item: vm,
      }),
    )
    if (isUserVm) {
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.SelfOpenstackVirtualMachines,
          params: { id: vm?.id },
          item: vm,
        }),
      )
    }
  }
}

const updateVmCachesMulti = (vms) => {
  const state = store.getState()
  const currentUser = state.session?.username
  const userId = state.session?.userDetails?.userId
  const currentTenant = state.preferences?.[currentUser]?.root?.currentTenant
  const tenantVms = vms?.filter((vm) => vm?.tenant_id === currentTenant)
  const userVms = vms?.filter((vm) => vm?.user_id === userId)

  if (isAdmin()) {
    dispatch(
      cacheActions.upsertAll({
        uniqueIdentifier: 'id',
        cacheKey: DataKeys.AllOpenstackVirtualMachines,
        params: {},
        items: vms,
      }),
    )
    if (tenantVms?.length) {
      dispatch(
        cacheActions.upsertAll({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: {},
          items: tenantVms,
        }),
      )
    }
  } else {
    dispatch(
      cacheActions.upsertAll({
        uniqueIdentifier: 'id',
        cacheKey: DataKeys.OpenstackVirtualMachines,
        params: {},
        items: vms,
      }),
    )
    if (userVms?.length) {
      dispatch(
        cacheActions.upsertAll({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.SelfOpenstackVirtualMachines,
          params: {},
          items: userVms,
        }),
      )
    }
  }
}

// Lite APIs
export const virtualMachinesLite = ActionsSet.make<DataKeys.OpenstackVirtualMachinesLite>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OpenstackVirtualMachinesLite,
  cacheKey: DataKeys.OpenstackVirtualMachinesLite,
})

export const listVirtualMachinesLite = virtualMachinesLite.add(
  new ListAction<DataKeys.OpenstackVirtualMachinesLite>(async (params?: { host?: string }) => {
    const searchParams = params?.host ? { host: params.host } : undefined
    return nova.getAllInstancesLite('', searchParams)
  }),
)

// All VMs - Lite
export const allVirtualMachinesLite = ActionsSet.make<DataKeys.AllOpenstackVirtualMachinesLite>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.AllOpenstackVirtualMachinesLite,
  cacheKey: DataKeys.AllOpenstackVirtualMachinesLite,
})

export const listAllVirtualMachinesLite = allVirtualMachinesLite.add(
  new ListAction<DataKeys.AllOpenstackVirtualMachinesLite>(async (params?: { host?: string }) => {
    const searchParams = params?.host ? { host: params.host } : undefined
    return nova.getAllInstancesLite('all_tenants=true', searchParams)
  }),
)

// Self VMs - Lite (for member users)
export const selfVirtualMachinesLite = ActionsSet.make<DataKeys.SelfOpenstackVirtualMachinesLite>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.SelfOpenstackVirtualMachinesLite,
  cacheKey: DataKeys.SelfOpenstackVirtualMachinesLite,
})

export const listSelfVirtualMachinesLite = selfVirtualMachinesLite.add(
  new ListAction<DataKeys.SelfOpenstackVirtualMachinesLite>(async (params?: { host?: string }) => {
    const state = store.getState()
    const userId = state?.session?.userDetails?.id
    const searchParams = params?.host ? { host: params.host } : undefined
    // Get only VMs for the current user
    return nova.getAllInstancesLite(userId ? `user_id=${userId}` : '', searchParams)
  }),
)

// Active VMs
export const statewiseVirtualMachinesLite = ActionsSet.make<
  DataKeys.OpenstackStatewiseVirtualMachinesLite
>({
  uniqueIdentifier: 'state',
  entityName: entityNamesByKey.OpenstackStatewiseVirtualMachinesLite,
  cacheKey: DataKeys.OpenstackStatewiseVirtualMachinesLite,
})

export const listStatewiseVirtualMachinesLite = statewiseVirtualMachinesLite.add(
  new ListAction<DataKeys.OpenstackStatewiseVirtualMachinesLite>(
    async (params?: { host?: string }) => {
      const searchParams = params?.host ? { host: params.host } : undefined
      const [activeVms, buildingVms, stoppedVms, pausedVms, suspendedVms] = await Promise.all([
        nova.getAllInstancesLite('vm_state=ACTIVE', searchParams),
        nova.getAllInstancesLite('vm_state=BUILDING', searchParams),
        nova.getAllInstancesLite('vm_state=STOPPED', searchParams),
        nova.getAllInstancesLite('vm_state=PAUSED', searchParams),
        nova.getAllInstancesLite('vm_state=SUSPENDED', searchParams),
      ])

      return [
        {
          state: 'active',
          servers: activeVms,
        },
        {
          state: 'building',
          servers: buildingVms,
        },
        {
          state: 'stopped',
          servers: stoppedVms,
        },
        {
          state: 'paused',
          servers: pausedVms,
        },
        {
          state: 'suspended',
          servers: suspendedVms,
        },
      ]
    },
  ),
)

// All Active VMs
export const allStatewiseVirtualMachinesLite = ActionsSet.make<
  DataKeys.AllOpenstackStatewiseVirtualMachinesLite
>({
  uniqueIdentifier: 'state',
  entityName: entityNamesByKey.AllOpenstackStatewiseVirtualMachinesLite,
  cacheKey: DataKeys.AllOpenstackStatewiseVirtualMachinesLite,
})

export const listAllStatewiseVirtualMachinesLite = allStatewiseVirtualMachinesLite.add(
  new ListAction<DataKeys.AllOpenstackStatewiseVirtualMachinesLite>(
    async (params?: { host?: string }) => {
      const searchParams = params?.host ? { host: params.host } : undefined
      const [activeVms, buildingVms, stoppedVms, pausedVms, suspendedVms] = await Promise.all([
        nova.getAllInstancesLite('all_tenants=true&vm_state=ACTIVE', searchParams),
        nova.getAllInstancesLite('all_tenants=true&vm_state=BUILDING', searchParams),
        nova.getAllInstancesLite('all_tenants=true&vm_state=STOPPED', searchParams),
        nova.getAllInstancesLite('all_tenants=true&vm_state=PAUSED', searchParams),
        nova.getAllInstancesLite('all_tenants=true&vm_state=SUSPENDED', searchParams),
      ])

      return [
        {
          state: 'active',
          servers: activeVms,
        },
        {
          state: 'building',
          servers: buildingVms,
        },
        {
          state: 'stopped',
          servers: stoppedVms,
        },
        {
          state: 'paused',
          servers: pausedVms,
        },
        {
          state: 'suspended',
          servers: suspendedVms,
        },
      ]
    },
  ),
)

// Self Statewise VMs (for member users)
export const selfStatewiseVirtualMachinesLite = ActionsSet.make<
  DataKeys.SelfOpenstackStatewiseVirtualMachinesLite
>({
  uniqueIdentifier: 'state',
  entityName: entityNamesByKey.SelfOpenstackStatewiseVirtualMachinesLite,
  cacheKey: DataKeys.SelfOpenstackStatewiseVirtualMachinesLite,
})

export const listSelfStatewiseVirtualMachinesLite = selfStatewiseVirtualMachinesLite.add(
  new ListAction<DataKeys.SelfOpenstackStatewiseVirtualMachinesLite>(
    async (params?: { host?: string }) => {
      const state = store.getState()
      const userId = state?.session?.userDetails?.id
      const userIdParam = userId ? `user_id=${userId}&` : ''
      const searchParams = params?.host ? { host: params.host } : undefined

      const [activeVms, buildingVms, stoppedVms, pausedVms, suspendedVms] = await Promise.all([
        nova.getAllInstancesLite(`${userIdParam}vm_state=ACTIVE`, searchParams),
        nova.getAllInstancesLite(`${userIdParam}vm_state=BUILDING`, searchParams),
        nova.getAllInstancesLite(`${userIdParam}vm_state=STOPPED`, searchParams),
        nova.getAllInstancesLite(`${userIdParam}vm_state=PAUSED`, searchParams),
        nova.getAllInstancesLite(`${userIdParam}vm_state=SUSPENDED`, searchParams),
      ])

      return [
        {
          state: 'active',
          servers: activeVms,
        },
        {
          state: 'building',
          servers: buildingVms,
        },
        {
          state: 'stopped',
          servers: stoppedVms,
        },
        {
          state: 'paused',
          servers: pausedVms,
        },
        {
          state: 'suspended',
          servers: suspendedVms,
        },
      ]
    },
  ),
)

export const allVirtualMachineActions = ActionsSet.make<DataKeys.AllOpenstackVirtualMachines>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.AllOpenstackVirtualMachines,
  cacheKey: DataKeys.AllOpenstackVirtualMachines,
})

export const listAllVirtualMachines = allVirtualMachineActions.add(
  new ListAction<DataKeys.AllOpenstackVirtualMachines>(async (params) => {
    // Is an admin-only API
    if (!isAdmin()) {
      return []
    }

    const { limit = 50, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } =
      params || {}

    // Fetch data with pagination and search params
    const servers = await nova.getInstancesPaginated({
      limit: limit as number,
      marker: marker as string | undefined,
      sortKey: sortKey as string,
      sortDir: sortDir as 'asc' | 'desc',
      allTenants: true,
      searchParams: searchParams as Record<string, string> | undefined,
    })

    // Get total count from lite API to determine if more records exist
    const allServersLite = await nova.getAllInstancesLite('all_tenants=True')
    const totalCount = allServersLite?.length || 0

    // Calculate current position
    const state = store.getState()
    const currentLoadedCount =
      state.cache?.cachedData?.[DataKeys.AllOpenstackVirtualMachines]?.length || 0
    const estimatedLoadedAfterThis = marker ? currentLoadedCount : servers.length

    // Store pagination metadata in session
    const nextMarker =
      servers?.length === limit && servers?.length > 0 ? servers[servers.length - 1].id : null
    const hasMore = estimatedLoadedAfterThis < totalCount

    dispatch(
      sessionActions.updateSession({
        allVmsPagination: {
          currentMarker: marker || null,
          nextMarker,
          hasMore,
          pageSize: limit,
        },
      }),
    )

    return servers
  })
    .addDependency(DataKeys.ManagementUsers)
    .addDependency(DataKeys.Images)
    .addDependency(DataKeys.OpenstackVolumesLite)
    .addDependency(DataKeys.ServerGroups)
    .addDependency(DataKeys.FederatedDomainUsers),
  // .addDependency(DataKeys.VmMigrations), // handled independently on list page
)

// Lightweight pagination action for subsequent pages (no dependencies)
export const listAllVirtualMachinesNextPage = allVirtualMachineActions.add(
  new CustomAction<DataKeys.AllOpenstackVirtualMachines>(
    'listNextPage',
    async (params) => {
      if (!isAdmin()) {
        return []
      }

      const { limit = 50, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } =
        params || {}

      // Fetch data with pagination and search params
      const servers = await nova.getInstancesPaginated({
        limit: limit as number,
        marker: marker as string | undefined,
        sortKey: sortKey as string,
        sortDir: sortDir as 'asc' | 'desc',
        allTenants: true,
        searchParams: searchParams as Record<string, string> | undefined,
      })

      // Get total count from lite API only on first page (no marker)
      // For subsequent pages, use cached totalCount from session
      let totalCount: number | undefined
      if (!marker) {
        const allServersLite = await nova.getAllInstancesLite('all_tenants=True')
        totalCount = allServersLite?.length || 0
      } else {
        // Get totalCount from session for subsequent pages
        const state = store.getState()
        totalCount = state.session?.allVmsPagination?.totalCount
      }

      // Calculate current position
      const state = store.getState()
      const currentLoadedCount =
        state.cache?.cachedData?.[DataKeys.AllOpenstackVirtualMachines]?.length || 0
      const estimatedLoadedAfterThis = marker ? currentLoadedCount : servers.length

      // Store pagination metadata in session
      const nextMarker =
        servers?.length === limit && servers?.length > 0 ? servers[servers.length - 1].id : null
      const hasMore =
        totalCount != null ? estimatedLoadedAfterThis < totalCount : servers?.length === limit

      dispatch(
        sessionActions.updateSession({
          allVmsPagination: {
            currentMarker: marker || null,
            nextMarker,
            hasMore,
            pageSize: limit,
            totalCount, // Preserve totalCount across pages
          },
        }),
      )

      return servers
    },
    // Cache update callback - replaceAll for first page, upsertAll for pagination
    (result: Server[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.AllOpenstackVirtualMachines,
            items: result,
          }),
        )
      } else {
        // First page (no marker) - replace all to show new sort order
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.AllOpenstackVirtualMachines,
            items: result,
          }),
        )
      }
      // Update refresh time to trigger re-renders
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          lastRefreshTime: new Date().getTime(),
        }),
      )
    },
  ),
)

// Lightweight search action for all VMs - no dependencies
export const searchAllVirtualMachines = allVirtualMachineActions.add(
  new CustomAction<DataKeys.AllOpenstackVirtualMachines>(
    'searchAllVirtualMachines',
    async (params) => {
      if (!isAdmin()) {
        return []
      }

      const { limit = 50, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } =
        params || {}

      const promises = []
      // Fetch data with search params (no dependencies for faster search)
      promises.push(
        nova.getInstancesPaginated({
          limit: limit as number,
          marker: marker as string | undefined,
          sortKey: sortKey as string,
          sortDir: sortDir as 'asc' | 'desc',
          allTenants: true,
          searchParams: searchParams as Record<string, string> | undefined,
        }),
      )

      if (!marker) {
        promises.push(
          nova.getAllInstancesLite(
            'all_tenants=True',
            searchParams as Record<string, string> | undefined,
          ),
        )
      }

      const [servers, allServersLite] = await Promise.all(promises)

      // Get total count from lite API only on first page (no marker)
      // For subsequent pages during search, use cached totalCount from session
      let totalCount: number | undefined
      if (!marker) {
        totalCount = allServersLite?.length || 0
      } else {
        // Get totalCount from session for subsequent search pages
        const state = store.getState()
        totalCount = state.session?.allVmsPagination?.totalCount
      }

      // Store metadata in session with search-filtered count
      const nextMarker =
        servers?.length === limit && servers?.length > 0 ? servers[servers.length - 1].id : null
      const hasMore = totalCount != null ? servers?.length === limit : false

      dispatch(
        sessionActions.updateSession({
          allVmsPagination: {
            currentMarker: marker || null,
            nextMarker,
            hasMore,
            pageSize: limit,
            totalCount, // Search-filtered total count (preserved across pages)
          },
        }),
      )

      return servers
    },
    // Use replaceAll for first page (no marker), upsertAll for pagination
    (result: Server[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.AllOpenstackVirtualMachines,
            items: result,
          }),
        )
      } else {
        // First page - replace all
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.AllOpenstackVirtualMachines,
            items: result,
          }),
        )
      }
    },
  ),
)

export const virtualMachineActions = ActionsSet.make<DataKeys.OpenstackVirtualMachines>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.OpenstackVirtualMachines,
  cacheKey: DataKeys.OpenstackVirtualMachines,
  addCacheKey: DataKeys.AllOpenstackVirtualMachines,
})

export const listVirtualMachines = virtualMachineActions.add(
  new ListAction<DataKeys.OpenstackVirtualMachines>(async (params) => {
    const { limit = 50, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } =
      params || {}
    const newTimestamp = getCurrentNovaFormattedTimestamp()

    // Fetch data with pagination and search params
    const servers = await nova.getInstancesPaginated({
      limit: limit as number,
      marker: marker as string | undefined,
      sortKey: sortKey as string,
      sortDir: sortDir as 'asc' | 'desc',
      searchParams: searchParams as Record<string, string> | undefined,
    })

    // Get total count from lite API to determine if more records exist
    const allServersLite = await nova.getAllInstancesLite()
    const totalCount = allServersLite?.length || 0

    // Calculate current position
    const state = store.getState()
    const currentLoadedCount =
      state.cache?.cachedData?.[DataKeys.OpenstackVirtualMachines]?.length || 0
    const estimatedLoadedAfterThis = marker ? currentLoadedCount : servers.length

    // Store pagination metadata in session
    const nextMarker =
      servers?.length === limit && servers?.length > 0 ? servers[servers?.length - 1].id : null
    const hasMore = estimatedLoadedAfterThis < totalCount

    dispatch(
      sessionActions.updateSession({
        vmsLastPolledTimestamp: newTimestamp,
        vmsPagination: {
          currentMarker: marker || null,
          nextMarker,
          hasMore,
          pageSize: limit,
        },
      }),
    )

    return servers
  })
    .addDependency(DataKeys.ManagementUsers)
    .addDependency(DataKeys.Images)
    .addDependency(DataKeys.ClusterHostGroups)
    .addDependency(DataKeys.ServerGroups)
    .addDependency(DataKeys.LeasePolicies)
    .addDependency(DataKeys.OpenstackVolumesLite)
    .addDependency(DataKeys.Leases)
    .addDependency(DataKeys.FederatedDomainUsers),
  // .addDependency(DataKeys.VmMigrations), // handled independently on list page
)

// Lightweight pagination action for subsequent pages (no dependencies)
export const listVirtualMachinesNextPage = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines>(
    'listNextPage',
    async (params) => {
      const { limit = 50, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } =
        params || {}
      const newTimestamp = getCurrentNovaFormattedTimestamp()

      // Fetch data with pagination and search params
      const servers = await nova.getInstancesPaginated({
        limit: limit as number,
        marker: marker as string | undefined,
        sortKey: sortKey as string,
        sortDir: sortDir as 'asc' | 'desc',
        searchParams: searchParams as Record<string, string> | undefined,
      })

      // Get total count from lite API only on first page (no marker)
      // For subsequent pages, use cached totalCount from session
      let totalCount: number | undefined
      if (!marker) {
        const allServersLite = await nova.getAllInstancesLite()
        totalCount = allServersLite?.length || 0
      } else {
        // Get totalCount from session for subsequent pages
        const state = store.getState()
        totalCount = state.session?.vmsPagination?.totalCount
      }

      // Calculate current position
      const state = store.getState()
      const currentLoadedCount =
        state.cache?.cachedData?.[DataKeys.OpenstackVirtualMachines]?.length || 0
      const estimatedLoadedAfterThis = marker ? currentLoadedCount : servers.length

      // Store pagination metadata in session
      const nextMarker =
        servers?.length === limit && servers?.length > 0 ? servers[servers?.length - 1].id : null
      const hasMore =
        totalCount != null ? estimatedLoadedAfterThis < totalCount : servers?.length === limit

      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: newTimestamp,
          vmsPagination: {
            currentMarker: marker || null,
            nextMarker,
            hasMore,
            pageSize: limit,
            totalCount, // Preserve totalCount across pages
          },
        }),
      )

      return servers
    },
    // Cache update callback - replaceAll for first page, upsertAll for pagination
    (result: Server[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.OpenstackVirtualMachines,
            items: result,
          }),
        )
      } else {
        // First page (no marker) - replace all to show new sort order
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.OpenstackVirtualMachines,
            items: result,
          }),
        )
      }
      // Update refresh time to trigger re-renders
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.OpenstackVirtualMachines,
          lastRefreshTime: new Date().getTime(),
        }),
      )
    },
  ),
)

export const createVirtualMachine = virtualMachineActions.add(
  new CreateAction<DataKeys.OpenstackVirtualMachines, { body }>(async ({ body }) => {
    const created = await nova.createInstance(body)
    const adminPass = created?.adminPass
    const instanceId = created?.id
    const newVm = await nova.getInstance(instanceId)

    // Custom update for self VMs
    // Todo: Perhaps maybe addCacheKey a list?
    if (!isAdmin()) {
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.SelfOpenstackVirtualMachines,
          params: { id: newVm?.id },
          item: newVm,
        }),
      )
    }

    return {
      ...newVm,
      adminPass,
    }
  }),
)

export const createVirtualMachines = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines>(
    'createMultipleVms',
    // @ts-ignore
    async ({ body }) => {
      const response = await nova.createInstances(body)
      const reservationId = response.reservation_id
      const vms = await nova.getInstancesForReservationId(reservationId)
      return vms
    },
    (result, { id }) => {
      updateVmCachesMulti(result)
    },
  ),
)

export const updateVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body: any }>(
    'updateVM',
    async ({ id, body }) => {
      const updatedVm = await nova.updateInstance(id, body)
      return updatedVm
    },
  ),
)

// Lightweight search action - no dependencies, for faster search
export const searchVirtualMachines = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines>(
    'searchVirtualMachines',
    async (params) => {
      const { limit = 50, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } =
        params || {}

      const promises = []
      // Fetch data with search params (no dependencies for faster search)
      promises.push(
        nova.getInstancesPaginated({
          limit: limit as number,
          marker: marker as string | undefined,
          sortKey: sortKey as string,
          sortDir: sortDir as 'asc' | 'desc',
          searchParams: searchParams as Record<string, string> | undefined,
        }),
      )

      // Fetch total count from lite API only on first page (no marker)
      if (!marker) {
        promises.push(
          nova.getAllInstancesLite('', searchParams as Record<string, string> | undefined),
        )
      }

      const [servers, allServersLite] = await Promise.all(promises)

      // Get total count from lite API only on first page (no marker)
      // For subsequent pages during search, use cached totalCount from session
      let totalCount: number | undefined
      if (!marker) {
        totalCount = allServersLite?.length || 0
      } else {
        // Get totalCount from session for subsequent search pages
        const state = store.getState()
        totalCount = state.session?.vmsPagination?.totalCount
      }

      // Store metadata in session with search-filtered count
      const nextMarker =
        servers?.length === limit && servers?.length > 0 ? servers[servers?.length - 1].id : null
      const hasMore = totalCount != null ? servers?.length === limit : false

      dispatch(
        sessionActions.updateSession({
          vmsPagination: {
            currentMarker: marker || null,
            nextMarker,
            hasMore,
            pageSize: limit,
            totalCount, // Search-filtered total count (preserved across pages)
          },
        }),
      )

      return servers
    },
    // Use replaceAll for first page (no marker), upsertAll for pagination
    (result: Server[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.OpenstackVirtualMachines,
            items: result,
          }),
        )
      } else {
        // First page - replace all
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.OpenstackVirtualMachines,
            items: result,
          }),
        )
      }
    },
  ),
)

export const deleteVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string }>(
    'deleteVm',
    async ({ id }) => {
      await nova.deleteInstance(id)
      // Putting this getInstance block in try catch so that it doesnt propogate error to the components
      // This is to handle the case where the VM is already deleted and getInstance throws an 401 error
      try {
        const vm = await nova.getInstance(id)
        return vm
      } catch (error) {
        return null
      }
    },
    (result, { id }) => {
      // Remove the vm from the cache if result is null
      if (!result) {
        dispatch(
          cacheActions.removeItem({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.OpenstackVirtualMachines,
            params: { id },
          }),
        )
        dispatch(
          cacheActions.removeItem({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.AllOpenstackVirtualMachines,
            params: { id },
          }),
        )
        dispatch(
          cacheActions.removeItem({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.SelfOpenstackVirtualMachines,
            params: { id },
          }),
        )
        return
      }
      // Update the vm in the cache
      updateVmCachesSingle(result)
    },
  ),
)

export const refreshVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string }>(
    'vmRefresh',
    async ({ id }) => {
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      // Update the vm in the cache
      updateVmCachesSingle(result)
    },
  ),
)

export const powerVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'vmOperation',
    async ({ id, body }) => {
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const addSecurityGroups = virtualMachineActions.add(
  new CustomAction<
    DataKeys.OpenstackVirtualMachines,
    { id: string; securityGroupNames?: string[] }
  >(
    'addSecurityGroups',
    async ({ id, securityGroupNames }) => {
      for (const securityGroupName of securityGroupNames) {
        await nova.addSecurityGroupAction({ id, securityGroupName })
      }

      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const removeSecurityGroups = virtualMachineActions.add(
  new CustomAction<
    DataKeys.OpenstackVirtualMachines,
    { id: string; securityGroupNames?: string[] }
  >(
    'removeSecurityGroups',
    async ({ id, securityGroupNames }) => {
      for (const securityGroupName of securityGroupNames) {
        await nova.removeSecurityGroupAction({ id, securityGroupName })
      }

      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle({ ...result, security_groups: result['security_groups'] || [] })
    },
  ),
)

export const snapshotVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'snapshotVm',
    async ({ id, body }) => {
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      const isVolumeBackedInstance =
        vm?.image === '' && vm?.['OS-EXT-SRV-ATTR:root_device_name'] === '/dev/vda'

      isVolumeBackedInstance ? listVolumeSnapshots.call({}) : listImages.call({})
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const getConsoleEndpoint = async ({ id }) => {
  const response = await nova.createConsole({ vmId: id })
  return response?.remote_console?.url
}

export const migrateVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'migrateVm',
    async ({ id, body }) => {
      await nova.instanceActionWithSpecificVersion({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const addFixedIpToVm = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'addFixedIp',
    async ({ id, body }) => {
      await nova.createInterface({ id, body })
      const vm = await nova.getInstance(id)
      listPorts.call({})
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const detachFixedIpFromVm = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; portId: string }>(
    'detachFixedIp',
    async ({ id, portId }) => {
      await nova.detachInterface({ id, portId })
      const vm = await nova.getInstance(id)
      listPorts.call({})
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const updateVmMetadata = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'updateVmMetadata',
    async ({ id, body }) => {
      await nova.setInstanceMetadata({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const rebuildVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'rebuildVm',
    async ({ id, body }) => {
      await nova.instanceActionWithSpecificVersion({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const resizeVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'resizeVm',
    async ({ id, body }) => {
      // Remove the migration from the cache
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const confirmResizeVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'confirmResizeVm',
    async ({ id, body }) => {
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const rescueVmAction = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body: any }>(
    'rescueVm',
    async ({ id, body }) => {
      const response = await nova.rescueVmAction({ id, body })
      const vm = await nova.getInstance(id)
      return { ...vm, adminPass: response?.adminPass }
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const unrescueVmAction = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'unrescueVm',
    async ({ id, body }) => {
      await nova.unrescueVmAction({ id })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const hotplugVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'hotplugVm',
    async ({ id, body }) => {
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      updateVmCachesSingle(result)
    },
  ),
)

export const pollVirtualMachines = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines>(
    'pollVirtualMachines',
    async (params) => {
      const state = store.getState()
      const lastTimestamp = state.session.vmsLastPolledTimestamp
      if (!lastTimestamp) {
        return []
      }
      const newTimestamp = getCurrentNovaFormattedTimestamp()
      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: newTimestamp,
        }),
      )
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.OpenstackVirtualMachines,
          lastRefreshTime: new Date().getTime(),
        }),
      )

      // Use params if provided to poll current page, otherwise get all instances since last poll
      if (params && Object.keys(params).length > 0) {
        const { limit, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } = params
        // Poll current page with filters - refresh all VMs on current page
        const vms = await nova.getInstancesPaginated({
          limit: limit as number,
          marker: marker as string | undefined,
          sortKey: sortKey as string,
          sortDir: sortDir as 'asc' | 'desc',
          searchParams: searchParams as Record<string, string> | undefined,
          changesSince: lastTimestamp, // Only get updated VMs
        })
        return vms
      } else {
        const vms = await nova.getInstancesSince(lastTimestamp)
        return vms
      }
    },
    (result: Server[], {}) => {
      updateVmCachesMulti(result)
    },
  ),
)

export const pollAllVirtualMachines = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines>(
    'pollAllVirtualMachines',
    async (params) => {
      const state = store.getState()
      const lastTimestamp = state.session.vmsLastPolledTimestamp
      if (!lastTimestamp) {
        return []
      }
      const newTimestamp = getCurrentNovaFormattedTimestamp()
      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: newTimestamp,
        }),
      )
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          lastRefreshTime: new Date().getTime(),
        }),
      )

      // Use params if provided to poll current page, otherwise get all instances since last poll
      if (params && Object.keys(params).length > 0) {
        const { limit, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } = params
        // Poll current page with filters - refresh all VMs on current page
        const vms = await nova.getInstancesPaginated({
          limit: limit as number,
          marker: marker as string | undefined,
          sortKey: sortKey as string,
          sortDir: sortDir as 'asc' | 'desc',
          searchParams: searchParams as Record<string, string> | undefined,
          changesSince: lastTimestamp, // Only get updated VMs
          allTenants: true,
        })
        return vms
      } else {
        // Fallback: poll all VMs that changed since last poll
        const vms = await nova.getAllInstancesSince(lastTimestamp)
        return vms
      }
    },
    (result: Server[], {}) => {
      updateVmCachesMulti(result)
    },
  ),
)

export const selfVirtualMachineActions = ActionsSet.make<DataKeys.SelfOpenstackVirtualMachines>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.SelfOpenstackVirtualMachines,
  cacheKey: DataKeys.SelfOpenstackVirtualMachines,
  addCacheKey: DataKeys.OpenstackVirtualMachines,
})

export const listSelfVirtualMachines = selfVirtualMachineActions.add(
  new ListAction<DataKeys.SelfOpenstackVirtualMachines>(async (params) => {
    const state = store.getState()
    const userId = state?.session?.userDetails?.id
    const newTimestamp = getCurrentNovaFormattedTimestamp()
    const { limit = 50, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } =
      params || {}

    // Fetch data with pagination and search params
    const servers = await nova.getInstancesPaginated({
      limit: limit as number,
      marker: marker as string | undefined,
      sortKey: sortKey as string,
      sortDir: sortDir as 'asc' | 'desc',
      userId,
      searchParams: searchParams as Record<string, string> | undefined,
    })

    // Get total count from lite API to determine if more records exist
    const allServersLite = await nova.getAllInstancesLite(`user_id=${userId}`)
    const totalCount = allServersLite?.length || 0

    // Calculate current position
    const currentLoadedCount =
      state.cache?.cachedData?.[DataKeys.SelfOpenstackVirtualMachines]?.length || 0
    const estimatedLoadedAfterThis = marker ? currentLoadedCount : servers.length

    // Store pagination metadata in session
    const nextMarker =
      servers?.length === limit && servers?.length > 0 ? servers[servers?.length - 1].id : null
    const hasMore = estimatedLoadedAfterThis < totalCount

    dispatch(
      sessionActions.updateSession({
        vmsLastPolledTimestamp: newTimestamp,
        selfVmsPagination: {
          currentMarker: marker || null,
          nextMarker,
          hasMore,
          pageSize: limit,
        },
      }),
    )

    return servers
  })
    .addDependency(DataKeys.ManagementUsers)
    .addDependency(DataKeys.Images)
    .addDependency(DataKeys.ClusterHostGroups)
    .addDependency(DataKeys.ServerGroups)
    .addDependency(DataKeys.LeasePolicies)
    .addDependency(DataKeys.OpenstackVolumesLite)
    .addDependency(DataKeys.Leases)
    .addDependency(DataKeys.FederatedDomainUsers),
  // .addDependency(DataKeys.VmMigrations), // handled independently on list page
)

// Lightweight pagination action for subsequent pages (no dependencies)
export const listSelfVirtualMachinesNextPage = selfVirtualMachineActions.add(
  new CustomAction<DataKeys.SelfOpenstackVirtualMachines>(
    'listNextPage',
    async (params) => {
      const state = store.getState()
      const userId = state?.session?.userDetails?.id
      const newTimestamp = getCurrentNovaFormattedTimestamp()
      const { limit = 50, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } =
        params || {}

      // Fetch data with pagination and search params
      const servers = await nova.getInstancesPaginated({
        limit: limit as number,
        marker: marker as string | undefined,
        sortKey: sortKey as string,
        sortDir: sortDir as 'asc' | 'desc',
        userId,
        searchParams: searchParams as Record<string, string> | undefined,
      })

      // Get total count from lite API only on first page (no marker)
      // For subsequent pages, use cached totalCount from session
      let totalCount: number | undefined
      if (!marker) {
        const allServersLite = await nova.getAllInstancesLite(`user_id=${userId}`)
        totalCount = allServersLite?.length || 0
      } else {
        // Get totalCount from session for subsequent pages
        totalCount = state.session?.selfVmsPagination?.totalCount
      }

      // Calculate current position
      const currentLoadedCount =
        state.cache?.cachedData?.[DataKeys.SelfOpenstackVirtualMachines]?.length || 0
      const estimatedLoadedAfterThis = marker ? currentLoadedCount : servers.length

      // Store pagination metadata in session
      const nextMarker =
        servers?.length === limit && servers?.length > 0 ? servers[servers?.length - 1].id : null
      const hasMore =
        totalCount != null ? estimatedLoadedAfterThis < totalCount : servers?.length === limit

      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: newTimestamp,
          selfVmsPagination: {
            currentMarker: marker || null,
            nextMarker,
            hasMore,
            pageSize: limit,
            totalCount, // Preserve totalCount across pages
          },
        }),
      )

      return servers
    },
    // Cache update callback - replaceAll for first page, upsertAll for pagination
    (result: Server[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.SelfOpenstackVirtualMachines,
            items: result,
          }),
        )
      } else {
        // First page (no marker) - replace all to show new sort order
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.SelfOpenstackVirtualMachines,
            items: result,
          }),
        )
      }
      // Update refresh time to trigger re-renders
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.SelfOpenstackVirtualMachines,
          lastRefreshTime: new Date().getTime(),
        }),
      )
    },
  ),
)

export const pollSelfVirtualMachines = selfVirtualMachineActions.add(
  new CustomAction<DataKeys.SelfOpenstackVirtualMachines>(
    'pollSelfVirtualMachines',
    async (params) => {
      const state = store.getState()
      const userId = state?.session?.userDetails?.id
      const lastTimestamp = state?.session?.vmsLastPolledTimestamp

      if (!lastTimestamp) {
        return []
      }
      const newTimestamp = getCurrentNovaFormattedTimestamp()
      dispatch(
        sessionActions.updateSession({
          vmsLastPolledTimestamp: newTimestamp,
        }),
      )
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.SelfOpenstackVirtualMachines,
          lastRefreshTime: new Date().getTime(),
        }),
      )

      // Use params if provided to poll current page, otherwise get all instances since last poll
      if (params && Object.keys(params).length > 0) {
        const { limit, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } = params
        const vms = await nova.getInstancesPaginated({
          limit: limit as number,
          marker: marker as string | undefined,
          sortKey: sortKey as string,
          sortDir: sortDir as 'asc' | 'desc',
          userId,
          searchParams: searchParams as Record<string, string> | undefined,
          changesSince: lastTimestamp, // Only get updated VMs
        })
        return vms
      } else {
        const vms = await nova.getSelfInstancesSince(lastTimestamp, userId)
        return vms
      }
    },
    (result: Server[], {}) => {
      updateVmCachesMulti(result)
    },
  ),
)

// Lightweight search action for self VMs - no dependencies
export const searchSelfVirtualMachines = selfVirtualMachineActions.add(
  new CustomAction<DataKeys.SelfOpenstackVirtualMachines>(
    'searchSelfVirtualMachines',
    async (params) => {
      const state = store.getState()
      const userId = state?.session?.userDetails?.id
      const { limit = 50, marker, searchParams, sortKey = 'created_at', sortDir = 'desc' } =
        params || {}

      const promises = []
      // Fetch data with search params (no dependencies for faster search)
      promises.push(
        nova.getInstancesPaginated({
          limit: limit as number,
          marker: marker as string | undefined,
          sortKey: sortKey as string,
          sortDir: sortDir as 'asc' | 'desc',
          userId,
          searchParams: searchParams as Record<string, string> | undefined,
        }),
      )

      if (!marker) {
        promises.push(
          nova.getAllInstancesLite(
            `user_id=${userId}`,
            searchParams as Record<string, string> | undefined,
          ),
        )
      }

      const [servers, allServersLite] = await Promise.all(promises)

      // Get total count from lite API only on first page (no marker)
      // For subsequent pages during search, use cached totalCount from session
      let totalCount: number | undefined
      if (!marker) {
        totalCount = allServersLite?.length || 0
      } else {
        // Get totalCount from session for subsequent search pages
        totalCount = state.session?.selfVmsPagination?.totalCount
      }

      // Store metadata in session with search-filtered count
      const nextMarker =
        servers?.length === limit && servers?.length > 0 ? servers[servers?.length - 1].id : null
      const hasMore = totalCount != null ? servers?.length === limit : false

      dispatch(
        sessionActions.updateSession({
          selfVmsPagination: {
            currentMarker: marker || null,
            nextMarker,
            hasMore,
            pageSize: limit,
            totalCount, // Search-filtered total count (preserved across pages)
          },
        }),
      )

      return servers
    },
    // Use replaceAll for first page (no marker), upsertAll for pagination
    (result: Server[], params) => {
      const { marker } = params || {}
      if (marker) {
        // Paginating - append to existing results
        dispatch(
          cacheActions.upsertAll({
            uniqueIdentifier: 'id',
            cacheKey: DataKeys.SelfOpenstackVirtualMachines,
            items: result,
          }),
        )
      } else {
        // First page - replace all
        dispatch(
          cacheActions.replaceAll({
            cacheKey: DataKeys.SelfOpenstackVirtualMachines,
            items: result,
          }),
        )
      }
    },
  ),
)

export const singleVmActions = ActionsSet.make<DataKeys.SingleOpenstackVirtualMachine>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.SingleOpenstackVirtualMachine,
  cacheKey: DataKeys.SingleOpenstackVirtualMachine,
})

export const listSingleVirtualMachine = singleVmActions.add(
  new ListAction<DataKeys.SingleOpenstackVirtualMachine, { id: string }>(async ({ id }) => {
    const vm = await nova.getInstance(id)
    return [vm]
  })
    .addDependency(DataKeys.ManagementUsers)
    .addDependency(DataKeys.Images)
    .addDependency(DataKeys.ClusterHostGroups)
    .addDependency(DataKeys.ServerGroups)
    .addDependency(DataKeys.LeasePolicies)
    .addDependency(DataKeys.OpenstackVolumesLite)
    .addDependency(DataKeys.Leases)
    .addDependency(DataKeys.FederatedDomainUsers)
    .addDependency(DataKeys.VmMigrations),
)

export const updateSingleVm = singleVmActions.add(
  new CustomAction<DataKeys.SingleOpenstackVirtualMachine, { id: string }>(
    'updateSingleVm',
    async ({ id }) => {
      const vm = await nova.getInstance(id)
      return [vm]
    },
    (result: Server[], { id }) => {
      // Update the vm in the cache
      dispatch(
        cacheActions.replaceAll({
          cacheKey: DataKeys.SingleOpenstackVirtualMachine,
          items: result,
        }),
      )
    },
  ),
)

export const toggleDeleteOnTermination = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; volumeId: string; body?: any }>(
    'toggleDeleteOnTermination',
    async ({ id, volumeId, body }) => {
      await nova.updateVolumeAttachment({ id, volumeId, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      // Update the vm in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id },
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          params: { id },
          item: result,
        }),
      )
    },
  ),
)

export const addServerGroupVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'addServerGroupVirtualMachine',
    async ({ id, body }) => {
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      const serverGroups = await nova.getServerGroups()
      dispatch(
        cacheActions.replaceAll({
          cacheKey: DataKeys.ServerGroups,
          items: serverGroups,
        }),
      )
      return vm
    },
    (result, { id }) => {
      // Update the vm in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id },
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          params: { id },
          item: result,
        }),
      )
    },
  ),
)

export const removeServerGroupVirtualMachine = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'removeServerGroupVirtualMachine',
    async ({ id, body }) => {
      await nova.instanceAction({ id, body })
      const vm = await nova.getInstance(id)
      const serverGroups = await nova.getServerGroups()
      dispatch(
        cacheActions.replaceAll({
          cacheKey: DataKeys.ServerGroups,
          items: serverGroups,
        }),
      )
      return vm
    },
    (result, { id }) => {
      // Update the vm in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id },
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          params: { id },
          item: result,
        }),
      )
    },
  ),
)

export const updateVirtualMachineOwner = virtualMachineActions.add(
  new CustomAction<DataKeys.OpenstackVirtualMachines, { id: string; body?: any }>(
    'updateVirtualMachineOwner',
    async ({ id, body }) => {
      await nova.updateVirtualMachineOwner({ id, body })
      const vm = await nova.getInstance(id)
      return vm
    },
    (result, { id }) => {
      // Update the vm in the cache
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.OpenstackVirtualMachines,
          params: { id },
          item: result,
        }),
      )
      dispatch(
        cacheActions.updateItem({
          uniqueIdentifier: 'id',
          cacheKey: DataKeys.AllOpenstackVirtualMachines,
          params: { id },
          item: result,
        }),
      )
    },
  ),
)

export const refreshVmDependencies = () => {
  listUsers.call({})
  listImages.call({})
  listClusterHostGroups.call({})
  listServerGroups.call({})
  listLeasePolicies.call({})
  listSimpleVolumes.call({})
  listLeases.call({})
  listFederatedDomainUsers.call({})
}

export const anyVmDependencyNotLoaded = () => {
  return (
    !getLastRefreshTime(DataKeys.ManagementUsers) ||
    !getLastRefreshTime(DataKeys.Images) ||
    !getLastRefreshTime(DataKeys.ClusterHostGroups) ||
    !getLastRefreshTime(DataKeys.ServerGroups) ||
    !getLastRefreshTime(DataKeys.LeasePolicies) ||
    !getLastRefreshTime(DataKeys.OpenstackVolumesLite) ||
    !getLastRefreshTime(DataKeys.Leases) ||
    !getLastRefreshTime(DataKeys.FederatedDomainUsers)
  )
}

export const getVirtualMachine = ({ id }) => {
  return nova.getInstance(id)
}

export const simpleMigrateVirtualMachine = ({ id, body }) => {
  return nova.instanceActionWithSpecificVersion({ id, body })
}

export const getVmEvents = ({ id }) => {
  return nova.getVmEvents({ id })
}

export const getVmEventDetails = ({ vmId, requestId }) => {
  return nova.getVmEventDetails({ vmId, requestId })
}

export const searchVmAcrossTenants = ({ query, property }) => {
  return nova.searchInstanceAcrossTenants({ query, property })
}

export const simpleAddVmTag = ({ id, tag }) => {
  return nova.addInstanceTag({ id, tag })
}
